// ─────────────────────────────────────────────────────────────────────────────
// Design-system theme model.
//
// A DesignTheme is a self-contained JSON description of every token the app
// themes against: accents (shared between light/dark), the light & dark
// semantic grounds, the radius scale, and the two font families. It can be
// applied at runtime, exported to a file, and re-imported — so the whole look
// is data, not code.
// ─────────────────────────────────────────────────────────────────────────────

export interface SemanticColors {
  bg: string;
  bgDeep: string;
  surface: string;
  text: string;
  textSoft: string;
  textDim: string;
  line: string;
  bubbleYou: string;
  /** Eyebrow / kicker accent for this mode (gold in dark, crimson in light). */
  eyebrow: string;
}

export interface Accents {
  gold: string;
  violet: string;
  crimson: string;
  success: string;
  ink: string;
  cream: string;
  onGold: string;
}

export interface Radii {
  sm: string;
  md: string;
  lg: string;
  pill: string;
}

export interface Fonts {
  serif: string;
  sans: string;
}

export interface DesignTheme {
  /** Schema marker so imported files can be recognised/validated. */
  schema: 'sekretly-design';
  version: number;
  name: string;
  accents: Accents;
  light: SemanticColors;
  dark: SemanticColors;
  radii: Radii;
  fonts: Fonts;
}

// The built-in default — mirrors the static tokens declared in src/index.css.
export const DEFAULT_THEME: DesignTheme = {
  schema: 'sekretly-design',
  version: 1,
  name: 'Sekretly — cream & ink (default)',
  accents: {
    gold: '#FF993B',
    violet: '#6F3FFD',
    crimson: '#A61029',
    success: '#9EC98A',
    ink: '#1C1B19',
    cream: '#F4ECD2',
    onGold: '#1C1B19',
  },
  light: {
    bg: '#F4ECD2',
    bgDeep: '#EDE8DF',
    surface: '#FBF5E4',
    text: '#1C1B19',
    textSoft: '#4A4334',
    textDim: '#6B6248',
    line: 'rgba(28, 27, 25, 0.14)',
    bubbleYou: 'rgba(28, 27, 25, 0.06)',
    eyebrow: 'rgb(var(--crimson))',
  },
  dark: {
    bg: '#1C1B19',
    bgDeep: '#131211',
    surface: '#282724',
    text: '#F4ECD2',
    textSoft: '#CFC6AE',
    textDim: '#8B8473',
    line: 'rgba(244, 236, 210, 0.10)',
    bubbleYou: 'rgba(244, 236, 210, 0.10)',
    eyebrow: 'rgb(var(--gold))',
  },
  radii: { sm: '10px', md: '14px', lg: '26px', pill: '999px' },
  fonts: {
    serif: '"Hedvig Letters Serif", Georgia, serif',
    sans: '"Inter Tight", system-ui, -apple-system, sans-serif',
  },
};

// ── Validation / normalisation ───────────────────────────────────────────────
// Imported JSON is untrusted: merge it onto the default so missing keys are
// filled and the result is always a complete, renderable theme.

function pickStrings<T extends object>(base: T, raw: unknown): T {
  const out = { ...base };
  if (!raw || typeof raw !== 'object') return out;
  const src = raw as Record<string, unknown>;
  for (const key of Object.keys(base) as (keyof T)[]) {
    const v = src[key as string];
    if (typeof v === 'string' && v.trim()) out[key] = v as T[keyof T];
  }
  return out;
}

/** Normalise arbitrary parsed JSON into a complete, safe DesignTheme. */
export function normalizeTheme(raw: unknown, fallbackName?: string): DesignTheme {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    schema: 'sekretly-design',
    version: 1,
    name:
      (typeof r.name === 'string' && r.name.trim()) ||
      fallbackName ||
      'Imported theme',
    accents: pickStrings(DEFAULT_THEME.accents, r.accents),
    light: pickStrings(DEFAULT_THEME.light, r.light),
    dark: pickStrings(DEFAULT_THEME.dark, r.dark),
    radii: pickStrings(DEFAULT_THEME.radii, r.radii),
    fonts: pickStrings(DEFAULT_THEME.fonts, r.fonts),
  };
}

/** True when the value looks like a Sekretly design theme. */
export function looksLikeTheme(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const r = raw as Record<string, unknown>;
  return !!(r.accents || r.light || r.dark || r.schema === 'sekretly-design');
}

// ── Applying a theme to the document ─────────────────────────────────────────

const STYLE_EL_ID = 'sk-dynamic-theme';

function hexToRgbChannels(color: string): string {
  const trimmed = color.trim();
  if (trimmed.startsWith('#')) {
    const h = trimmed.replace('#', '');
    const n = parseInt(h, 16);
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
  }
  return trimmed;
}

function rgbChannelsToHex(channels: string): string {
  const trimmed = channels.trim();
  if (trimmed.startsWith('#')) return trimmed;
  const parts = trimmed.split(/\s+/).map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return trimmed;
  return '#' + parts.map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase();
}

function semanticVars(c: SemanticColors): string {
  return [
    `--bg:${c.bg}`,
    `--bg-deep:${c.bgDeep}`,
    `--surface:${c.surface}`,
    `--text:${c.text}`,
    `--text-soft:${c.textSoft}`,
    `--text-dim:${c.textDim}`,
    `--text-dim-rgb:${hexToRgbChannels(c.textDim)}`,
    `--line:${c.line}`,
    `--bubble-you:${c.bubbleYou}`,
    `--eyebrow:${c.eyebrow}`,
  ].join(';');
}

/** Build the CSS that overrides index.css's static tokens for this theme. */
export function themeToCss(theme: DesignTheme): string {
  const a = theme.accents;
  const root = [
    `--gold:${hexToRgbChannels(a.gold)}`,
    `--violet:${hexToRgbChannels(a.violet)}`,
    `--crimson:${hexToRgbChannels(a.crimson)}`,
    `--success:${hexToRgbChannels(a.success)}`,
    `--ink:${hexToRgbChannels(a.ink)}`,
    `--cream:${hexToRgbChannels(a.cream)}`,
    `--on-gold:${a.onGold}`,
    semanticVars(theme.light),
    `--r-sm:${theme.radii.sm}`,
    `--r-md:${theme.radii.md}`,
    `--r-lg:${theme.radii.lg}`,
    `--r-pill:${theme.radii.pill}`,
    `--font-serif:${theme.fonts.serif}`,
    `--font-sans:${theme.fonts.sans}`,
  ].join(';');

  return `:root{${root}}\n.dark{${semanticVars(theme.dark)}}`;
}

/** Inject (or replace) the dynamic theme stylesheet in <head>. */
export function applyTheme(theme: DesignTheme): void {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(STYLE_EL_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_EL_ID;
    document.head.appendChild(el);
  }
  el.textContent = themeToCss(theme);
}

/** Download a theme as a pretty-printed JSON file. */
export function exportTheme(theme: DesignTheme): void {
  const blob = new Blob([JSON.stringify(theme, null, 2)], { type: 'application/json' });
  downloadBlob(blob, `sekretly-theme-${safeName(theme)}.json`);
}

/** Download a theme as a CSS custom-properties file. */
export function exportThemeCss(theme: DesignTheme): void {
  const css = themeToCssPretty(theme);
  const blob = new Blob([css], { type: 'text/css' });
  downloadBlob(blob, `sekretly-theme-${safeName(theme)}.css`);
}

function safeName(theme: DesignTheme): string {
  return theme.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'export';
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function themeToCssPretty(theme: DesignTheme): string {
  const a = theme.accents;
  const indent = '  ';
  const rootVars = [
    `/* Theme: ${theme.name} */`,
    '',
    `/* Accents */`,
    `${indent}--gold: ${hexToRgbChannels(a.gold)};`,
    `${indent}--violet: ${hexToRgbChannels(a.violet)};`,
    `${indent}--crimson: ${hexToRgbChannels(a.crimson)};`,
    `${indent}--success: ${hexToRgbChannels(a.success)};`,
    `${indent}--ink: ${hexToRgbChannels(a.ink)};`,
    `${indent}--cream: ${hexToRgbChannels(a.cream)};`,
    `${indent}--on-gold: ${a.onGold};`,
    '',
    `/* Light mode (default) */`,
    ...semanticVarsPretty(theme.light, indent),
    '',
    `/* Radii */`,
    `${indent}--r-sm: ${theme.radii.sm};`,
    `${indent}--r-md: ${theme.radii.md};`,
    `${indent}--r-lg: ${theme.radii.lg};`,
    `${indent}--r-pill: ${theme.radii.pill};`,
    '',
    `/* Fonts */`,
    `${indent}--font-serif: ${theme.fonts.serif};`,
    `${indent}--font-sans: ${theme.fonts.sans};`,
  ];

  const darkVars = [
    `/* Dark mode */`,
    ...semanticVarsPretty(theme.dark, indent),
  ];

  return `:root {\n${rootVars.join('\n')}\n}\n\n.dark {\n${darkVars.join('\n')}\n}\n`;
}

function semanticVarsPretty(c: SemanticColors, indent: string): string[] {
  return [
    `${indent}--bg: ${c.bg};`,
    `${indent}--bg-deep: ${c.bgDeep};`,
    `${indent}--surface: ${c.surface};`,
    `${indent}--text: ${c.text};`,
    `${indent}--text-soft: ${c.textSoft};`,
    `${indent}--text-dim: ${c.textDim};`,
    `${indent}--text-dim-rgb: ${hexToRgbChannels(c.textDim)};`,
    `${indent}--line: ${c.line};`,
    `${indent}--bubble-you: ${c.bubbleYou};`,
    `${indent}--eyebrow: ${c.eyebrow};`,
  ];
}

/** Parse a CSS custom-properties file back into a DesignTheme. */
export function parseCssToTheme(css: string, fallbackName?: string): DesignTheme | null {
  const vars = new Map<string, string>();
  const darkVars = new Map<string, string>();

  const darkMatch = css.match(/\.dark\s*\{([^}]*)}/s);
  const rootMatch = css.match(/:root\s*\{([^}]*)}/s);

  function extractVars(block: string, target: Map<string, string>) {
    const re = /--([\w-]+)\s*:\s*([^;]+);/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(block)) !== null) {
      target.set(m[1].trim(), m[2].trim());
    }
  }

  if (rootMatch) extractVars(rootMatch[1], vars);
  if (darkMatch) extractVars(darkMatch[1], darkVars);

  if (vars.size === 0) return null;

  const nameMatch = css.match(/\/\*\s*Theme:\s*(.+?)\s*\*\//);
  const name = nameMatch?.[1] || fallbackName || 'Imported CSS theme';

  function pickSemantic(src: Map<string, string>, fallback: SemanticColors): SemanticColors {
    return {
      bg: src.get('bg') ?? fallback.bg,
      bgDeep: src.get('bg-deep') ?? fallback.bgDeep,
      surface: src.get('surface') ?? fallback.surface,
      text: src.get('text') ?? fallback.text,
      textSoft: src.get('text-soft') ?? fallback.textSoft,
      textDim: src.get('text-dim') ?? fallback.textDim,
      line: src.get('line') ?? fallback.line,
      bubbleYou: src.get('bubble-you') ?? fallback.bubbleYou,
      eyebrow: src.get('eyebrow') ?? fallback.eyebrow,
    };
  }

  return {
    schema: 'sekretly-design',
    version: 1,
    name,
    accents: {
      gold: rgbChannelsToHex(vars.get('gold') ?? DEFAULT_THEME.accents.gold),
      violet: rgbChannelsToHex(vars.get('violet') ?? DEFAULT_THEME.accents.violet),
      crimson: rgbChannelsToHex(vars.get('crimson') ?? DEFAULT_THEME.accents.crimson),
      success: rgbChannelsToHex(vars.get('success') ?? DEFAULT_THEME.accents.success),
      ink: rgbChannelsToHex(vars.get('ink') ?? DEFAULT_THEME.accents.ink),
      cream: rgbChannelsToHex(vars.get('cream') ?? DEFAULT_THEME.accents.cream),
      onGold: vars.get('on-gold') ?? DEFAULT_THEME.accents.onGold,
    },
    light: pickSemantic(vars, DEFAULT_THEME.light),
    dark: pickSemantic(darkVars.size > 0 ? darkVars : vars, DEFAULT_THEME.dark),
    radii: {
      sm: vars.get('r-sm') ?? DEFAULT_THEME.radii.sm,
      md: vars.get('r-md') ?? DEFAULT_THEME.radii.md,
      lg: vars.get('r-lg') ?? DEFAULT_THEME.radii.lg,
      pill: vars.get('r-pill') ?? DEFAULT_THEME.radii.pill,
    },
    fonts: {
      serif: vars.get('font-serif') ?? DEFAULT_THEME.fonts.serif,
      sans: vars.get('font-sans') ?? DEFAULT_THEME.fonts.sans,
    },
  };
}
